import type {
  ScanCaptureDetails,
  ScanCriticalPathCandidateBatchShadowEvidence,
  ScanOpenCvParallelShadowEvidence,
  ScanOpenCvParallelShadowTask,
} from "../types/scanWorkflow";
import { evaluateCanonicalTemplateGeometry } from "./canonicalTemplateGeometryService";
import { isLabelOcrParallelShadowRuntimeWarm, runMaterialNumberParallelShadowBatch } from "./labelOcrService";
import { recordParallelRuntimeLifecycle } from "./parallelRuntimeSoakService";
import { evaluateParallelRuntimeAdmission } from "./parallelRuntimeAdmissionService";
import { computeCandidateRerankScore, selectCandidateRerankWinner } from "./parallelCandidateRerankContract";
import {
  getRecognitionParallelismProbeSnapshot,
  warmRecognitionParallelismProbe,
} from "./recognitionParallelismProbeService";

const CONTRACT_VERSION = "same-input-opencv-parallel-shadow-v1" as const;
const PROFILE_ID = "opencv-2-worker-candidate-batch-shadow-v1" as const;
const PRODUCTION_PROFILE = "serial-production-v1" as const;
const OPENCV_URL = "/opencv/opencv-4.9.0.js";
const SHADOW_YIELD_MS = 350;
const WORKER_TIMEOUT_MS = 20000;
const PRODUCTION_RECTIFICATION_TIMEOUT_MS = 5000;
const SHADOW_POOL_IDLE_MS = 120000;
const OUTPUT_WIDTH = 1800;
const OUTPUT_HEIGHT = 432;
const TRANSFER_MODE = "transferable-arraybuffer-copy" as const;

let shadowInFlight = false;
let latestShadow: ScanOpenCvParallelShadowEvidence | null = null;
let shadowWorkerPoolPromise: Promise<WorkerHandle[]> | null = null;
let shadowWorkerPoolReady = false;
let shadowWorkerPoolUseCount = 0;
let shadowWorkerPoolIdleTimer: number | null = null;

interface CandidateInput {
  candidateId: string;
  candidateIndex: number | null;
  productionSelected: boolean;
  combinedScore: number | null;
  corners: Array<{ x: number; y: number }>;
  requiresRefinement: boolean;
}

interface WorkerTimings {
  reconstructImageDataMs: number;
  inputToMatMs: number;
  homographyMs: number;
  warpPerspectiveMs: number;
  readbackMs: number;
  sha256Ms: number;
  workerTotalMs: number;
}

interface WorkerRectifyResult {
  type: "rectify-result";
  requestId: string;
  executionId: string;
  workerId: string;
  candidateId: string;
  candidateIndex: number | null;
  executionContext: "webworker";
  transferMode: typeof TRANSFER_MODE;
  outputSha256: string;
  outputWidth: number;
  outputHeight: number;
  outputBytes: number;
  outputRgbaBuffer: ArrayBuffer;
  timings: WorkerTimings;
}

interface WorkerTaskResult {
  task: ScanOpenCvParallelShadowTask;
  imageData: ImageData;
}

interface WorkerHandle {
  id: string;
  worker: Worker;
  run: (candidate: CandidateInput, imageData: ImageData, executionId: string, originMs: number, phase: "serial" | "parallel", timeoutMs?: number) => Promise<WorkerTaskResult>;
  terminate: () => void;
}

function round(value: number | null, digits = 1): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

async function sha256Bytes(bytes: ArrayBuffer | ArrayBufferView): Promise<string> {
  // WebCrypto's BufferSource typing is intentionally ArrayBuffer-backed.
  // ArrayBufferView.buffer is ArrayBufferLike and may therefore be typed as
  // SharedArrayBuffer even though this shadow path transfers ordinary buffers.
  // Copy into a fresh ArrayBuffer-backed Uint8Array before hashing so the
  // browser contract and TypeScript's strict DOM types agree.
  const source = bytes instanceof ArrayBuffer
    ? new Uint8Array(bytes)
    : new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const digestInput = new Uint8Array(source.byteLength);
  digestInput.set(source);
  const digest = await crypto.subtle.digest("SHA-256", digestInput);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

async function decodeLosslessRgba(file: File): Promise<{ imageData: ImageData; decodeMode: "createImageBitmap-canvas" | "html-image-canvas"; decodeMs: number }> {
  const started = performance.now();
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("PV-3 konnte keinen 2D-Canvas-Kontext erzeugen.");

  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    try {
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      context.drawImage(bitmap, 0, 0);
      return {
        imageData: context.getImageData(0, 0, bitmap.width, bitmap.height),
        decodeMode: "createImageBitmap-canvas",
        decodeMs: Math.round(performance.now() - started),
      };
    } finally {
      bitmap.close();
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("PV-3 konnte das Rectification-Input-PNG nicht dekodieren."));
      image.src = url;
    });
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    context.drawImage(image, 0, 0);
    return {
      imageData: context.getImageData(0, 0, image.naturalWidth, image.naturalHeight),
      decodeMode: "html-image-canvas",
      decodeMs: Math.round(performance.now() - started),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function normalizedCornerDistance(left: Array<{ x: number; y: number }>, right: Array<{ x: number; y: number }>): number {
  if (left.length !== 4 || right.length !== 4) return Number.POSITIVE_INFINITY;
  return left.reduce((sum, point, index) => sum + Math.hypot(point.x - right[index].x, point.y - right[index].y), 0) / 4;
}

function buildCandidateInputs(details: ScanCaptureDetails): CandidateInput[] {
  const productionCorners = details.rectification.inputCorners ?? null;
  if (!productionCorners || productionCorners.length !== 4) return [];

  const selectedIndex = details.detection?.topKRerank?.selectedCandidateIndex
    ?? details.detection?.candidatePool.find((candidate) => candidate.selected)?.index
    ?? null;
  const selectedCandidate = selectedIndex === null
    ? null
    : details.detection?.candidatePool.find((candidate) => candidate.index === selectedIndex) ?? null;

  const roi = details.mapping?.searchRoi;
  const left = Number(roi?.left ?? 0);
  const top = Number(roi?.top ?? 0);
  const right = Number(roi?.right ?? 1);
  const bottom = Number(roi?.bottom ?? 1);
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);
  const mapPoolCorners = (candidateIndex: number): Array<{ x: number; y: number }> | null => {
    if (!(width > 0 && height > 0)) return null;
    const candidate = details.detection?.candidatePool.find((item) => item.index === candidateIndex) ?? null;
    if (!candidate || candidate.corners.length !== 4) return null;
    return candidate.corners.map((corner) => ({
      x: left + corner.x * width,
      y: top + corner.y * height,
    }));
  };

  const topK = details.detection?.topKRerank ?? null;
  if (topK?.attempted && topK.probes.length >= 2) {
    const exact: CandidateInput[] = [];
    for (const probe of topK.probes.slice(0, 2)) {
      const isProductionSelected = probe.candidateIndex === topK.selectedCandidateIndex;
      const corners = isProductionSelected ? productionCorners.map((corner) => ({ ...corner })) : mapPoolCorners(probe.candidateIndex);
      if (!corners) return [];
      exact.push({
        candidateId: isProductionSelected ? "production-selected" : `candidate-${probe.candidateIndex}`,
        candidateIndex: probe.candidateIndex,
        productionSelected: isProductionSelected,
        combinedScore: probe.geometryScore,
        corners,
        requiresRefinement: !isProductionSelected,
      });
    }
    return exact;
  }

  const candidates: CandidateInput[] = [{
    candidateId: "production-selected",
    candidateIndex: selectedIndex,
    productionSelected: true,
    combinedScore: selectedCandidate?.combinedScore ?? details.detection?.score ?? null,
    corners: productionCorners.map((corner) => ({ ...corner })),
    requiresRefinement: false,
  }];
  const alternates = (details.detection?.candidatePool ?? [])
    .filter((candidate) => candidate.accepted && candidate.index !== selectedIndex && candidate.corners.length === 4)
    .sort((a, b) => b.combinedScore - a.combinedScore || a.index - b.index);
  for (const candidate of alternates) {
    const rawCorners = mapPoolCorners(candidate.index);
    if (!rawCorners || normalizedCornerDistance(rawCorners, productionCorners) < 0.0025) continue;
    candidates.push({
      candidateId: `candidate-${candidate.index}`,
      candidateIndex: candidate.index,
      productionSelected: false,
      combinedScore: candidate.combinedScore,
      corners: rawCorners,
      requiresRefinement: true,
    });
    if (candidates.length >= 2) break;
  }
  return candidates;
}

const PV6_RAW_CORNER_REFINEMENT_RADIUS_PX = 34;
const PV6_RAW_CORNER_REFINEMENT_MAX_SHIFT_RATIO = 0.020;

async function refineCandidateInputsFromRawPhoto(candidates: CandidateInput[], rawPhotoFile: File | null): Promise<CandidateInput[]> {
  if (!rawPhotoFile || !candidates.some((candidate) => candidate.requiresRefinement)) return candidates;
  const objectUrl = URL.createObjectURL(rawPhotoFile);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("PV-6 konnte das Production-RAW für Candidate-Refinement nicht dekodieren."));
      image.src = objectUrl;
    });
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    const maxShift = Math.max(4, Math.min(PV6_RAW_CORNER_REFINEMENT_RADIUS_PX, Math.round(Math.min(width, height) * PV6_RAW_CORNER_REFINEMENT_MAX_SHIFT_RATIO)));
    return candidates.map((candidate) => {
      if (!candidate.requiresRefinement || candidate.corners.length !== 4) return candidate;
      const refined = candidate.corners.map((corner) => {
        const cx = Math.round(corner.x * width);
        const cy = Math.round(corner.y * height);
        const left = Math.max(1, cx - maxShift);
        const top = Math.max(1, cy - maxShift);
        const right = Math.min(width - 2, cx + maxShift);
        const bottom = Math.min(height - 2, cy + maxShift);
        const patchWidth = Math.max(3, right - left + 1);
        const patchHeight = Math.max(3, bottom - top + 1);
        const canvas = document.createElement("canvas");
        canvas.width = patchWidth;
        canvas.height = patchHeight;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) return corner;
        context.drawImage(image, left, top, patchWidth, patchHeight, 0, 0, patchWidth, patchHeight);
        const data = context.getImageData(0, 0, patchWidth, patchHeight).data;
        const gray = (x: number, y: number) => {
          const index = (y * patchWidth + x) * 4;
          return data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114;
        };
        let bestX = cx - left;
        let bestY = cy - top;
        let bestScore = 0;
        for (let y = 1; y < patchHeight - 1; y += 1) {
          for (let x = 1; x < patchWidth - 1; x += 1) {
            const gx = Math.abs(gray(x + 1, y) - gray(x - 1, y));
            const gy = Math.abs(gray(x, y + 1) - gray(x, y - 1));
            const distance = Math.hypot((left + x) - cx, (top + y) - cy);
            const proximity = Math.max(0.35, 1 - 0.45 * distance / Math.max(1, maxShift));
            const score = (gx + gy + Math.min(gx, gy) * 0.75) * proximity;
            if (score > bestScore) { bestScore = score; bestX = x; bestY = y; }
          }
        }
        const absoluteX = left + bestX;
        const absoluteY = top + bestY;
        const shift = Math.hypot(absoluteX - cx, absoluteY - cy);
        const accept = bestScore >= 42 && shift <= maxShift;
        return accept ? { x: absoluteX / width, y: absoluteY / height } : corner;
      });
      return { ...candidate, corners: refined, requiresRefinement: false };
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function overlapMetrics(tasks: ScanOpenCvParallelShadowTask[]): { activeWorkersPeak: number; overlapMs: number; overlapRatio: number | null } {
  if (tasks.length === 0) return { activeWorkersPeak: 0, overlapMs: 0, overlapRatio: null };
  const events = tasks.flatMap((task) => ([
    { at: task.startedOffsetMs, delta: 1 },
    { at: task.finishedOffsetMs, delta: -1 },
  ])).sort((a, b) => a.at - b.at || a.delta - b.delta);
  let active = 0;
  let peak = 0;
  let overlapMs = 0;
  let previousAt = events[0]?.at ?? 0;
  for (const event of events) {
    if (event.at > previousAt && active >= 2) overlapMs += event.at - previousAt;
    active += event.delta;
    peak = Math.max(peak, active);
    previousAt = event.at;
  }
  const earliest = Math.min(...tasks.map((task) => task.startedOffsetMs));
  const latest = Math.max(...tasks.map((task) => task.finishedOffsetMs));
  const span = Math.max(0, latest - earliest);
  return {
    activeWorkersPeak: peak,
    overlapMs: round(overlapMs, 1) ?? 0,
    overlapRatio: span > 0 ? round(overlapMs / span, 4) : null,
  };
}


function numericClose(left: number | null | undefined, right: number | null | undefined, tolerance = 1e-6): boolean {
  if (left == null || right == null) return left == null && right == null;
  return Math.abs(left - right) <= tolerance;
}

async function imageDataToPngBlobShadow(imageData: ImageData): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("PV-6 konnte keinen 2D-Kontext für lossless Candidate-Encoding erzeugen.");
  context.putImageData(imageData, 0, 0);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("PV-6 Candidate-PNG konnte nicht erzeugt werden.")), "image/png");
  });
}

async function runCriticalPathCandidateBatchShadow(input: {
  details: ScanCaptureDetails;
  candidates: CandidateInput[];
  serialResults: WorkerTaskResult[];
  parallelResults: WorkerTaskResult[];
  fullBatchStartedMs: number;
}): Promise<ScanCriticalPathCandidateBatchShadowEvidence> {
  const serialReference = input.details.detection?.topKRerank ?? null;
  if (!serialReference?.attempted || serialReference.probes.length < 2 || input.candidates.length < 2) {
    return {
      schemaVersion: 1,
      contractVersion: "full-fidelity-critical-path-candidate-batch-shadow-v1",
      profileId: "pv6-topk-rectify-template-rerank-ocr-2worker-shadow-v1",
      status: "skipped",
      productionInfluenced: false,
      fullFidelityPreserved: true,
      candidateCount: input.candidates.length,
      productionCandidateBatchWallMs: null,
      parallelCandidateBatchWallMs: null,
      estimatedSpeedup: null,
      outputEncodeWallMs: null,
      rerankOcrWallMs: null,
      templateEvaluationWallMs: null,
      selectedCandidateIndexSerial: serialReference?.selectedCandidateIndex ?? null,
      selectedCandidateIndexParallel: null,
      deterministicWinnerMatch: null,
      candidateParity: [],
      parallelParityPass: null,
      fallbackReason: "TOP_K_RERANK_NOT_REQUIRED",
      error: null,
    };
  }

  try {
    const serialByIndex = new Map(serialReference.probes.map((probe) => [probe.candidateIndex, probe]));
    const serialRectifiedByIndex = new Map(input.serialResults.map((result) => [result.task.candidateIndex, result.task]));
    const parallelByIndex = new Map(input.parallelResults.map((result) => [result.task.candidateIndex, result]));

    const templateStarted = performance.now();
    const templates = new Map<number, ReturnType<typeof evaluateCanonicalTemplateGeometry>>();
    for (const candidate of input.candidates) {
      const result = parallelByIndex.get(candidate.candidateIndex);
      if (candidate.candidateIndex == null || !result) continue;
      templates.set(candidate.candidateIndex, evaluateCanonicalTemplateGeometry(result.imageData));
    }
    const templateEvaluationWallMs = Math.round(performance.now() - templateStarted);

    const encodeStarted = performance.now();
    const encoded = await Promise.all(input.candidates.map(async (candidate) => {
      const result = parallelByIndex.get(candidate.candidateIndex);
      if (candidate.candidateIndex == null || !result) throw new Error(`PV-6 Candidate ${candidate.candidateId} ohne Parallel-Output.`);
      const blob = await imageDataToPngBlobShadow(result.imageData);
      return {
        candidate,
        file: new File([blob], `PV6_${candidate.candidateId}_${Date.now()}.png`, { type: "image/png", lastModified: Date.now() }),
      };
    }));
    const outputEncodeWallMs = Math.round(performance.now() - encodeStarted);

    const ocrBatch = await runMaterialNumberParallelShadowBatch(
      encoded.map((item) => ({ candidateId: item.candidate.candidateId, file: item.file })),
      input.details.detection?.visionSnapshot,
    );
    const ocrById = new Map(ocrBatch.probes.map((probe) => [probe.candidateId, probe]));

    const parallelProbes = input.candidates.map((candidate) => {
      if (candidate.candidateIndex == null) throw new Error(`PV-6 Candidate ${candidate.candidateId} ohne Index.`);
      const template = templates.get(candidate.candidateIndex);
      const ocr = ocrById.get(candidate.candidateId);
      if (!template || !ocr) throw new Error(`PV-6 Candidate ${candidate.candidateId} unvollständig.`);
      const geometryScore = candidate.combinedScore ?? 0;
      return {
        candidateIndex: candidate.candidateIndex,
        geometryScore,
        materialNumber: ocr.text.replace(/\s+/g, "").trim(),
        confidence: ocr.confidence,
        templateScore: template.template_score,
        templateResidual: template.template_residual_normalized,
        rerankScore: computeCandidateRerankScore({ geometryScore, confidence: ocr.confidence, templateScore: template.template_score }),
      };
    });

    const originalIndex = input.details.detection?.candidatePool.find((candidate) => candidate.selected)?.index
      ?? serialReference.probes[0]?.candidateIndex
      ?? null;
    const winnerParallel = originalIndex === null
      ? null
      : selectCandidateRerankWinner(parallelProbes, originalIndex);

    const candidateParity = input.candidates.map((candidate) => {
      if (candidate.candidateIndex == null) throw new Error(`PV-6 Candidate ${candidate.candidateId} ohne Index.`);
      const serial = serialByIndex.get(candidate.candidateIndex);
      const parallel = parallelProbes.find((probe) => probe.candidateIndex === candidate.candidateIndex);
      const serialRectified = serialRectifiedByIndex.get(candidate.candidateIndex);
      const parallelRectified = parallelByIndex.get(candidate.candidateIndex)?.task;
      if (!serial || !parallel || !serialRectified || !parallelRectified) throw new Error(`PV-6 Parity-Evidence für Candidate ${candidate.candidateIndex} fehlt.`);
      return {
        candidateIndex: candidate.candidateIndex,
        rectifiedShaMatch: serialRectified.outputSha256 === parallelRectified.outputSha256,
        materialNumberMatch: serial.materialNumber === parallel.materialNumber,
        confidenceMatch: numericClose(serial.confidence, parallel.confidence),
        templateScoreMatch: numericClose(serial.templateScore, parallel.templateScore, 1e-9),
        templateResidualMatch: numericClose(serial.templateResidual, parallel.templateResidual, 1e-9),
        rerankScoreMatch: numericClose(serial.rerankScore, parallel.rerankScore, 1e-9),
        serialMaterialNumber: serial.materialNumber,
        parallelMaterialNumber: parallel.materialNumber,
        serialConfidence: serial.confidence,
        parallelConfidence: parallel.confidence,
        serialTemplateScore: serial.templateScore,
        parallelTemplateScore: parallel.templateScore,
        serialTemplateResidual: serial.templateResidual,
        parallelTemplateResidual: parallel.templateResidual,
        serialRerankScore: serial.rerankScore,
        parallelRerankScore: parallel.rerankScore,
      };
    });

    const deterministicWinnerMatch = winnerParallel?.candidateIndex === serialReference.selectedCandidateIndex;
    const parallelParityPass = deterministicWinnerMatch
      && candidateParity.every((item) => item.rectifiedShaMatch
        && item.materialNumberMatch
        && item.confidenceMatch
        && item.templateScoreMatch
        && item.templateResidualMatch
        && item.rerankScoreMatch);
    const parallelCandidateBatchWallMs = Math.round(performance.now() - input.fullBatchStartedMs);
    const timing = input.details.rectification.timing;
    const productionCandidateBatchWallMs = timing
      ? Math.round((timing.cornerRefinementMs ?? 0)
        + (timing.workerRoundTripMs ?? 0)
        + (timing.outputEncodeMs ?? 0)
        + (timing.rerankOcrMs ?? 0)
        + (timing.templateEvaluationMs ?? 0))
      : null;

    return {
      schemaVersion: 1,
      contractVersion: "full-fidelity-critical-path-candidate-batch-shadow-v1",
      profileId: "pv6-topk-rectify-template-rerank-ocr-2worker-shadow-v1",
      status: parallelParityPass ? "pass" : "mismatch",
      productionInfluenced: false,
      fullFidelityPreserved: true,
      candidateCount: input.candidates.length,
      productionCandidateBatchWallMs,
      parallelCandidateBatchWallMs,
      estimatedSpeedup: productionCandidateBatchWallMs !== null && parallelCandidateBatchWallMs > 0
        ? round(productionCandidateBatchWallMs / parallelCandidateBatchWallMs, 3)
        : null,
      outputEncodeWallMs,
      rerankOcrWallMs: ocrBatch.wallMs,
      templateEvaluationWallMs,
      selectedCandidateIndexSerial: serialReference.selectedCandidateIndex,
      selectedCandidateIndexParallel: winnerParallel?.candidateIndex ?? null,
      deterministicWinnerMatch,
      candidateParity,
      parallelParityPass,
      fallbackReason: parallelParityPass ? null : "PV6_CANDIDATE_BATCH_PARITY_MISMATCH",
      error: null,
    };
  } catch (error: unknown) {
    return {
      schemaVersion: 1,
      contractVersion: "full-fidelity-critical-path-candidate-batch-shadow-v1",
      profileId: "pv6-topk-rectify-template-rerank-ocr-2worker-shadow-v1",
      status: "error",
      productionInfluenced: false,
      fullFidelityPreserved: true,
      candidateCount: input.candidates.length,
      productionCandidateBatchWallMs: null,
      parallelCandidateBatchWallMs: null,
      estimatedSpeedup: null,
      outputEncodeWallMs: null,
      rerankOcrWallMs: null,
      templateEvaluationWallMs: null,
      selectedCandidateIndexSerial: input.details.detection?.topKRerank?.selectedCandidateIndex ?? null,
      selectedCandidateIndexParallel: null,
      deterministicWinnerMatch: null,
      candidateParity: [],
      parallelParityPass: null,
      fallbackReason: "PV6_CANDIDATE_BATCH_RUNTIME_ERROR",
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

function createWorkerHandle(workerIndex: number): Promise<WorkerHandle> {
  const id = `pv3-opencv-worker-${workerIndex + 1}`;
  const worker = new Worker(new URL("../workers/openCvParallelShadowWorker.ts", import.meta.url));
  return new Promise<WorkerHandle>((resolve, reject) => {
    const initTimeout = window.setTimeout(() => {
      worker.terminate();
      reject(new Error(`${id} OpenCV-Init Timeout.`));
    }, WORKER_TIMEOUT_MS);

    const onError = (event: ErrorEvent) => {
      window.clearTimeout(initTimeout);
      worker.terminate();
      reject(new Error(event.message || `${id} ist beim Start fehlgeschlagen.`));
    };

    const onInitMessage = (event: MessageEvent<Record<string, unknown>>) => {
      const message = event.data;
      if (message.type === "error") {
        window.clearTimeout(initTimeout);
        worker.removeEventListener("message", onInitMessage);
        worker.removeEventListener("error", onError);
        worker.terminate();
        reject(new Error(String(message.message ?? `${id} OpenCV-Init fehlgeschlagen.`)));
        return;
      }
      if (message.type !== "ready") return;
      window.clearTimeout(initTimeout);
      worker.removeEventListener("message", onInitMessage);
      worker.removeEventListener("error", onError);

      const handle: WorkerHandle = {
        id,
        worker,
        run: (candidate, imageData, executionId, originMs, phase, requestedTimeoutMs = WORKER_TIMEOUT_MS) => new Promise<WorkerTaskResult>((taskResolve, taskReject) => {
          const requestId = crypto.randomUUID();
          const taskStarted = performance.now();
          const transferPrepStarted = performance.now();
          const transferable = imageData.data.slice().buffer;
          const transferPrepMs = performance.now() - transferPrepStarted;
          const timeoutMs = Math.max(1000, requestedTimeoutMs);
          const timeout = window.setTimeout(() => {
            worker.removeEventListener("message", onTaskMessage);
            worker.removeEventListener("error", onTaskError);
            taskReject(new Error(`${id} PV-3 Rectification Timeout nach ${Math.round(timeoutMs / 1000)} s.`));
          }, timeoutMs);

          function onTaskError(event: ErrorEvent): void {
            window.clearTimeout(timeout);
            worker.removeEventListener("message", onTaskMessage);
            worker.removeEventListener("error", onTaskError);
            taskReject(new Error(event.message || `${id} PV-3 Worker ist während der Rectification ausgefallen.`));
          }

          function onTaskMessage(taskEvent: MessageEvent<Record<string, unknown>>): void {
            const raw = taskEvent.data;
            if (raw.requestId !== requestId) return;
            window.clearTimeout(timeout);
            worker.removeEventListener("message", onTaskMessage);
            worker.removeEventListener("error", onTaskError);
            if (raw.type === "rectify-error") {
              taskReject(new Error(String(raw.message ?? `${id} PV-3 Rectification fehlgeschlagen.`)));
              return;
            }
            if (raw.type !== "rectify-result") return;
            const result = raw as unknown as WorkerRectifyResult;
            const taskFinished = performance.now();
            const pixels = new Uint8ClampedArray(result.outputRgbaBuffer);
            taskResolve({
              task: {
                executionId,
                workerId: id,
                phase,
                candidateId: candidate.candidateId,
                candidateIndex: candidate.candidateIndex,
                productionSelected: candidate.productionSelected,
                executionContext: "webworker",
                transferMode: TRANSFER_MODE,
                transferPrepMs: round(transferPrepMs, 1) ?? 0,
                startedOffsetMs: round(taskStarted - originMs, 1) ?? 0,
                finishedOffsetMs: round(taskFinished - originMs, 1) ?? 0,
                durationMs: Math.round(taskFinished - taskStarted),
                outputSha256: result.outputSha256,
                outputWidth: result.outputWidth,
                outputHeight: result.outputHeight,
                timings: {
                  reconstructImageDataMs: round(result.timings.reconstructImageDataMs, 1) ?? 0,
                  inputToMatMs: round(result.timings.inputToMatMs, 1) ?? 0,
                  homographyMs: round(result.timings.homographyMs, 1) ?? 0,
                  warpPerspectiveMs: round(result.timings.warpPerspectiveMs, 1) ?? 0,
                  readbackMs: round(result.timings.readbackMs, 1) ?? 0,
                  sha256Ms: round(result.timings.sha256Ms, 1) ?? 0,
                  workerTotalMs: round(result.timings.workerTotalMs, 1) ?? 0,
                },
              },
              imageData: new ImageData(pixels, result.outputWidth, result.outputHeight),
            });
          }

          worker.addEventListener("message", onTaskMessage);
          worker.addEventListener("error", onTaskError);
          worker.postMessage({
            type: "rectify",
            requestId,
            executionId,
            workerId: id,
            candidateId: candidate.candidateId,
            candidateIndex: candidate.candidateIndex,
            width: imageData.width,
            height: imageData.height,
            rgbaBuffer: transferable,
            corners: candidate.corners,
            outputWidth: OUTPUT_WIDTH,
            outputHeight: OUTPUT_HEIGHT,
            transferMode: TRANSFER_MODE,
          }, [transferable]);
        }),
        terminate: () => worker.terminate(),
      };
      resolve(handle);
    };

    worker.addEventListener("message", onInitMessage);
    worker.addEventListener("error", onError);
    worker.postMessage({ type: "init", opencvUrl: OPENCV_URL, workerId: id });
  });
}

function clearShadowPoolIdleTimer(): void {
  if (shadowWorkerPoolIdleTimer !== null) {
    window.clearTimeout(shadowWorkerPoolIdleTimer);
    shadowWorkerPoolIdleTimer = null;
  }
}

export function terminateOpenCvParallelShadowRuntime(): void {
  clearShadowPoolIdleTimer();
  const active = shadowWorkerPoolPromise;
  shadowWorkerPoolPromise = null;
  shadowWorkerPoolReady = false;
  shadowWorkerPoolUseCount = 0;
  if (!active) return;
  void active.then((workers) => {
    for (const worker of workers) worker.terminate();
  }).catch(() => undefined);
}

function armShadowPoolIdleTimer(): void {
  clearShadowPoolIdleTimer();
  shadowWorkerPoolIdleTimer = window.setTimeout(() => {
    shadowWorkerPoolIdleTimer = null;
    terminateOpenCvParallelShadowRuntime();
  }, SHADOW_POOL_IDLE_MS);
}

async function ensureOpenCvShadowWorkerPool(): Promise<WorkerHandle[]> {
  if (!shadowWorkerPoolPromise) {
    const created = Promise.all([createWorkerHandle(0), createWorkerHandle(1)]);
    shadowWorkerPoolPromise = created;
    void created.then(() => {
      if (shadowWorkerPoolPromise === created) {
        shadowWorkerPoolReady = true;
        recordParallelRuntimeLifecycle("opencv", "pool_ready");
        armShadowPoolIdleTimer();
      }
    }).catch(() => {
      if (shadowWorkerPoolPromise === created) {
        shadowWorkerPoolPromise = null;
        shadowWorkerPoolReady = false;
      }
    });
  }
  const workers = await shadowWorkerPoolPromise;
  if (workers.length !== 2) throw new Error(`PV-4 OpenCV-Warmpool unvollständig: ${workers.length}/2 Worker.`);
  return workers;
}

export function isOpenCvParallelShadowRuntimeWarm(): boolean {
  return shadowWorkerPoolReady;
}

export async function warmOpenCvParallelShadowRuntime(): Promise<void> {
  if (typeof Worker === "undefined") return;
  await ensureOpenCvShadowWorkerPool();
}

export interface ParallelProductionCandidateInput {
  candidateId: string;
  candidateIndex: number;
  productionSelected: boolean;
  geometryScore: number;
  corners: Array<{ x: number; y: number }>;
}

export interface ParallelProductionCandidateProbe {
  candidateIndex: number;
  geometryScore: number;
  materialNumber: string;
  confidence: number | null;
  templateScore: number;
  templateResidual: number | null;
  rerankScore: number;
  file: File;
  imageData: ImageData;
}

export interface ParallelProductionCandidateBatchResult {
  candidateCount: number;
  selectedCandidateIndex: number;
  probes: ParallelProductionCandidateProbe[];
  wallMs: number;
  rectifyWallMs: number;
  outputEncodeWallMs: number;
  rerankOcrWallMs: number;
  templateEvaluationWallMs: number;
  workerPoolReused: true;
  workerStartupMs: 0;
  effectiveWorkers: 2;
  overlapMs: number;
  overlapRatio: number | null;
}

export async function runOpenCvProductionCriticalPathCandidateBatch(input: {
  rectificationInput: ImageData;
  candidates: ParallelProductionCandidateInput[];
  originalCandidateIndex: number;
  runtimeSnapshot?: Parameters<typeof runMaterialNumberParallelShadowBatch>[1];
  recognitionRunId?: string | null;
}): Promise<ParallelProductionCandidateBatchResult> {
  if (input.candidates.length !== 2) {
    throw new Error(`PV-8 Production Candidate-Batch erwartet exakt 2 Kandidaten, erhalten: ${input.candidates.length}.`);
  }
  if (!shadowWorkerPoolReady || !isLabelOcrParallelShadowRuntimeWarm()) {
    throw new Error("PV-8 Production Candidate-Batch darf keinen kalten Parallel-Pool im Critical Path starten.");
  }
  const workers = await ensureOpenCvShadowWorkerPool();
  const started = performance.now();
  const rectifyStarted = performance.now();
  let parallelResults: WorkerTaskResult[];
  try {
    parallelResults = await Promise.all(input.candidates.map((candidate, index) => workers[index].run(
      {
        candidateId: candidate.candidateId,
        candidateIndex: candidate.candidateIndex,
        productionSelected: candidate.productionSelected,
        combinedScore: candidate.geometryScore,
        corners: candidate.corners,
        requiresRefinement: false,
      },
      input.rectificationInput,
      `${input.recognitionRunId ?? "no-run"}:pv8:production:${candidate.candidateId}`,
      rectifyStarted,
      "parallel",
      PRODUCTION_RECTIFICATION_TIMEOUT_MS,
    )));
  } catch (error) {
    // v0.282.4: Ein im Critical Path hängender Full-RAW-Worker wird sofort
    // als Pool verworfen. Der Aufrufer fällt danach im selben Scan auf den
    // seriellen Full-Fidelity-Pfad zurück, statt einen 20-s-Zombie wiederzuverwenden.
    terminateOpenCvParallelShadowRuntime();
    throw error;
  }
  const rectifyWallMs = Math.round(performance.now() - rectifyStarted);
  const parallelTasks = parallelResults.map((result) => result.task);
  const overlap = overlapMetrics(parallelTasks);
  if (overlap.activeWorkersPeak < 2 || (overlap.overlapRatio ?? 0) <= 0) {
    throw new Error("PV-8 Production Candidate-Batch hat keine echte 2-Worker-Überlappung erreicht.");
  }

  const templateStarted = performance.now();
  const templateByIndex = new Map<number, ReturnType<typeof evaluateCanonicalTemplateGeometry>>();
  for (const [index, candidate] of input.candidates.entries()) {
    templateByIndex.set(candidate.candidateIndex, evaluateCanonicalTemplateGeometry(parallelResults[index].imageData));
  }
  const templateEvaluationWallMs = Math.round(performance.now() - templateStarted);

  const encodeStarted = performance.now();
  const encoded = await Promise.all(input.candidates.map(async (candidate, index) => {
    const blob = await imageDataToPngBlobShadow(parallelResults[index].imageData);
    return {
      candidate,
      file: new File([blob], `PV8_${candidate.candidateId}_${Date.now()}_${index}.png`, { type: "image/png", lastModified: Date.now() }),
    };
  }));
  const outputEncodeWallMs = Math.round(performance.now() - encodeStarted);

  const ocrBatch = await runMaterialNumberParallelShadowBatch(
    encoded.map(({ candidate, file }) => ({ candidateId: candidate.candidateId, file })),
    input.runtimeSnapshot,
  );
  const ocrById = new Map(ocrBatch.probes.map((probe) => [probe.candidateId, probe]));
  const probes = input.candidates.map((candidate, index) => {
    const template = templateByIndex.get(candidate.candidateIndex);
    const ocr = ocrById.get(candidate.candidateId);
    if (!template || !ocr) throw new Error(`PV-8 Candidate ${candidate.candidateId} unvollständig.`);
    const materialNumber = ocr.text.replace(/\s+/g, "").trim();
    const rerankScore = computeCandidateRerankScore({
      geometryScore: candidate.geometryScore,
      confidence: ocr.confidence,
      templateScore: template.template_score,
    });
    return {
      candidateIndex: candidate.candidateIndex,
      geometryScore: candidate.geometryScore,
      materialNumber,
      confidence: ocr.confidence,
      templateScore: template.template_score,
      templateResidual: template.template_residual_normalized,
      rerankScore,
      file: encoded[index].file,
      imageData: parallelResults[index].imageData,
    } satisfies ParallelProductionCandidateProbe;
  });
  const winner = selectCandidateRerankWinner(probes, input.originalCandidateIndex);
  if (!winner) throw new Error("PV-8 Production Candidate-Batch konnte keinen deterministischen Gewinner bestimmen.");
  armShadowPoolIdleTimer();
  return {
    candidateCount: input.candidates.length,
    selectedCandidateIndex: winner.candidateIndex,
    probes,
    wallMs: Math.round(performance.now() - started),
    rectifyWallMs,
    outputEncodeWallMs,
    rerankOcrWallMs: ocrBatch.wallMs,
    templateEvaluationWallMs,
    workerPoolReused: true,
    workerStartupMs: 0,
    effectiveWorkers: 2,
    overlapMs: overlap.overlapMs,
    overlapRatio: overlap.overlapRatio,
  };
}

export async function exerciseOpenCvParallelShadowRecovery(): Promise<void> {
  await ensureOpenCvShadowWorkerPool();
  terminateOpenCvParallelShadowRuntime();
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
  await ensureOpenCvShadowWorkerPool();
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    recordParallelRuntimeLifecycle("opencv", "pagehide_terminated");
    terminateOpenCvParallelShadowRuntime();
  }, { passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return;
    recordParallelRuntimeLifecycle("opencv", "visibility_hidden_terminated");
    terminateOpenCvParallelShadowRuntime();
  }, { passive: true });
}

function skippedEvidence(reason: string, recognitionRunId: string | null, details: ScanCaptureDetails | null): ScanOpenCvParallelShadowEvidence {
  return {
    schemaVersion: 1,
    contractVersion: CONTRACT_VERSION,
    profileId: PROFILE_ID,
    status: "skipped",
    recognitionRunId,
    startedAtUtc: new Date().toISOString(),
    finishedAtUtc: new Date().toISOString(),
    productionInfluenced: false,
    productionExecutionProfile: PRODUCTION_PROFILE,
    sourceMode: "archived-production-rectification-rgba",
    decodeMode: null,
    executionContext: "main-thread+webworker",
    transferMode: TRANSFER_MODE,
    sourceInputRgbaSha256: null,
    sourceInputRgbaShaMatch: null,
    productionOutputRgbaSha256: details?.rectification.outputRgbaSha256 ?? null,
    requestedWorkers: 2,
    createdWorkers: 0,
    effectiveWorkers: 0,
    activeWorkersPeak: 0,
    workersTerminated: true,
    workersRetainedForReuse: false,
    workerPoolWarm: shadowWorkerPoolReady,
    workerPoolReused: false,
    workerPoolReuseCount: shadowWorkerPoolUseCount,
    candidateCount: 0,
    workerStartupMs: null,
    inputDecodeMs: null,
    serialWallMs: null,
    parallelWallMs: null,
    estimatedSpeedup: null,
    overlapMs: null,
    overlapRatio: null,
    productionStageTotalMs: details?.rectification.timing?.totalMs ?? null,
    serialTasks: [],
    parallelTasks: [],
    parity: {
      allRectifiedShaMatch: null,
      productionSelectedOutputMatch: null,
      deterministicOrderMatch: null,
      sameInputRgba: null,
      parallelParityPass: null,
    },
    criticalPathCandidateBatch: null,
    fallbackReason: reason,
    error: null,
  };
}

export function getLatestOpenCvParallelShadowEvidence(): ScanOpenCvParallelShadowEvidence | null {
  return latestShadow ? structuredClone(latestShadow) : null;
}

export async function scheduleOpenCvParallelShadow(input: {
  rectificationInputFile: File | null;
  rawPhotoFile?: File | null;
  scanDetails: ScanCaptureDetails | null;
  recognitionRunId?: string | null;
}): Promise<ScanOpenCvParallelShadowEvidence> {
  const recognitionRunId = input.recognitionRunId ?? input.scanDetails?.recognitionTiming?.recognitionRunId ?? null;
  if (!input.scanDetails?.rectification.used || !input.rectificationInputFile) {
    latestShadow = skippedEvidence("NO_RECTIFICATION_INPUT", recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }
  if (shadowInFlight) {
    latestShadow = skippedEvidence("SHADOW_BUSY", recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }

  await new Promise<void>((resolve) => window.setTimeout(resolve, SHADOW_YIELD_MS));
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    latestShadow = skippedEvidence("DOCUMENT_NOT_VISIBLE", recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }

  const admission = evaluateParallelRuntimeAdmission();
  if (!admission.allowed) {
    latestShadow = skippedEvidence(`PV7_ADMISSION_${admission.reason ?? "DENIED"}`, recognitionRunId, input.scanDetails);
    return structuredClone(latestShadow);
  }

  const probe = await warmRecognitionParallelismProbe().catch(() => getRecognitionParallelismProbeSnapshot());
  if (probe.status !== "ready" || probe.classification !== "likely_parallel" || probe.checksumMatch !== true || probe.createdWorkers < 2) {
    latestShadow = skippedEvidence(
      probe.status === "unsupported"
        ? (probe.fallbackReason ?? "PARALLELISM_UNSUPPORTED")
        : probe.status === "error"
          ? (probe.fallbackReason ?? "PARALLELISM_PROBE_ERROR")
          : `PARALLELISM_GATE_${probe.classification.toUpperCase()}`,
      recognitionRunId,
      input.scanDetails,
    );
    return structuredClone(latestShadow);
  }

  let candidates = buildCandidateInputs(input.scanDetails);
  if (candidates.length >= 2) {
    candidates = await refineCandidateInputsFromRawPhoto(candidates, input.rawPhotoFile ?? null).catch(() => candidates);
  }
  if (candidates.length < 2) {
    latestShadow = skippedEvidence("INSUFFICIENT_INDEPENDENT_OPENCV_WORK", recognitionRunId, input.scanDetails);
    latestShadow.candidateCount = candidates.length;
    return structuredClone(latestShadow);
  }

  shadowInFlight = true;
  let workers: WorkerHandle[] = [];
  const startedAtUtc = new Date().toISOString();
  const workerPoolReused = shadowWorkerPoolReady;
  try {
    const startupStarted = performance.now();
    workers = await ensureOpenCvShadowWorkerPool();
    const workerStartupMs = workerPoolReused ? 0 : Math.round(performance.now() - startupStarted);
    shadowWorkerPoolUseCount += 1;
    armShadowPoolIdleTimer();
    const decode = await decodeLosslessRgba(input.rectificationInputFile);
    const sourceInputRgbaSha256 = await sha256Bytes(decode.imageData.data);
    const expectedInputSha = input.scanDetails.rectification.inputRgbaSha256 ?? null;
    const sourceInputRgbaShaMatch = expectedInputSha === null ? null : expectedInputSha === sourceInputRgbaSha256;
    if (sourceInputRgbaShaMatch === false) {
      latestShadow = skippedEvidence("RECTIFICATION_INPUT_RGBA_SHA_MISMATCH", recognitionRunId, input.scanDetails);
      latestShadow = {
        ...latestShadow,
        sourceInputRgbaSha256,
        sourceInputRgbaShaMatch,
        decodeMode: decode.decodeMode,
        inputDecodeMs: decode.decodeMs,
        candidateCount: candidates.length,
      };
      return structuredClone(latestShadow);
    }

    const serialOrigin = performance.now();
    const serialResults: WorkerTaskResult[] = [];
    for (let index = 0; index < candidates.length; index += 1) {
      serialResults.push(await workers[0].run(
        candidates[index],
        decode.imageData,
        `${recognitionRunId ?? "no-run"}:pv3:serial:${candidates[index].candidateId}`,
        serialOrigin,
        "serial",
      ));
    }
    const serialWallMs = Math.round(performance.now() - serialOrigin);
    const serialTasks = serialResults.map((result) => result.task);

    const parallelOrigin = performance.now();
    const fullBatchStartedMs = parallelOrigin;
    const parallelResults = await Promise.all(candidates.map((candidate, index) => workers[index].run(
      candidate,
      decode.imageData,
      `${recognitionRunId ?? "no-run"}:pv3:parallel:${candidate.candidateId}`,
      parallelOrigin,
      "parallel",
    )));
    const parallelWallMs = Math.round(performance.now() - parallelOrigin);
    const parallelTasks = parallelResults.map((result) => result.task);

    const serialByCandidate = new Map(serialTasks.map((task) => [task.candidateId, task]));
    const parallelByCandidate = new Map(parallelTasks.map((task) => [task.candidateId, task]));
    const allRectifiedShaMatch = candidates.every((candidate) => serialByCandidate.get(candidate.candidateId)?.outputSha256 === parallelByCandidate.get(candidate.candidateId)?.outputSha256);
    const productionSelectedSerial = serialTasks.find((task) => task.productionSelected) ?? null;
    const productionOutputRgbaSha256 = input.scanDetails.rectification.outputRgbaSha256 ?? null;
    const productionSelectedOutputMatch = productionOutputRgbaSha256 === null
      ? null
      : productionSelectedSerial?.outputSha256 === productionOutputRgbaSha256;
    const deterministicOrderMatch = serialTasks.map((task) => task.candidateId).join("|") === parallelTasks.map((task) => task.candidateId).join("|");
    const overlap = overlapMetrics(parallelTasks);
    const parallelParityPass = allRectifiedShaMatch && deterministicOrderMatch && productionSelectedOutputMatch !== false;
    const criticalPathCandidateBatch = parallelParityPass
      ? await runCriticalPathCandidateBatchShadow({
          details: input.scanDetails,
          candidates,
          serialResults,
          parallelResults,
          fullBatchStartedMs,
        })
      : null;

    armShadowPoolIdleTimer();

    latestShadow = {
      schemaVersion: 1,
      contractVersion: CONTRACT_VERSION,
      profileId: PROFILE_ID,
      status: parallelParityPass ? "pass" : "mismatch",
      recognitionRunId,
      startedAtUtc,
      finishedAtUtc: new Date().toISOString(),
      productionInfluenced: false,
      productionExecutionProfile: PRODUCTION_PROFILE,
      sourceMode: "archived-production-rectification-rgba",
      decodeMode: decode.decodeMode,
      executionContext: "main-thread+webworker",
      transferMode: TRANSFER_MODE,
      sourceInputRgbaSha256,
      sourceInputRgbaShaMatch,
      productionOutputRgbaSha256,
      requestedWorkers: 2,
      createdWorkers: 2,
      effectiveWorkers: overlap.activeWorkersPeak >= 2 ? 2 : 1,
      activeWorkersPeak: overlap.activeWorkersPeak,
      workersTerminated: false,
      workersRetainedForReuse: true,
      workerPoolWarm: true,
      workerPoolReused,
      workerPoolReuseCount: shadowWorkerPoolUseCount,
      candidateCount: candidates.length,
      workerStartupMs,
      inputDecodeMs: decode.decodeMs,
      serialWallMs,
      parallelWallMs,
      estimatedSpeedup: parallelWallMs > 0 ? round(serialWallMs / parallelWallMs, 3) : null,
      overlapMs: overlap.overlapMs,
      overlapRatio: overlap.overlapRatio,
      productionStageTotalMs: input.scanDetails.rectification.timing?.totalMs ?? null,
      serialTasks,
      parallelTasks,
      parity: {
        allRectifiedShaMatch,
        productionSelectedOutputMatch,
        deterministicOrderMatch,
        sameInputRgba: sourceInputRgbaShaMatch ?? true,
        parallelParityPass,
      },
      criticalPathCandidateBatch,
      fallbackReason: parallelParityPass ? null : "OPENCV_PARITY_MISMATCH",
      error: null,
    };
    return structuredClone(latestShadow);
  } catch (error: unknown) {
    recordParallelRuntimeLifecycle("opencv", "runtime_error_recycle");
    terminateOpenCvParallelShadowRuntime();
    latestShadow = {
      ...skippedEvidence("SHADOW_RUNTIME_ERROR", recognitionRunId, input.scanDetails),
      status: "error",
      startedAtUtc,
      finishedAtUtc: new Date().toISOString(),
      createdWorkers: workers.length,
      workersTerminated: true,
      workersRetainedForReuse: false,
      workerPoolWarm: false,
      workerPoolReused,
      workerPoolReuseCount: shadowWorkerPoolUseCount,
      error: error instanceof Error ? error.message : String(error),
    };
    return structuredClone(latestShadow);
  } finally {
    shadowInFlight = false;
  }
}
